import React from "react";
import ScreenSpinner from "../../Components/ScreenSpinner";
import { useAuth } from "../auth";
import devDebug from "../../Helpers/debug";
import { useTabStore } from "../../Stores/tabStore";
import { useMapsStore } from "../../Stores/mapsStore";
import { useDefaultStrategyStore } from "../../Stores/defaultStrategyStore";
import { useTenderStrategyStore } from "../../Stores/tenderStrategyStore";
import { useCPVStore } from "../../Stores/cpvStore";
import { useAuthStore } from "../../Stores/authStore";

export const getStrategies = (auth, controller) => {
  devDebug(`strategyService -> fetching strategies`);
  auth.apiService.axiosGET(
    `user/preferences/strategies/`, //endpoint
    controller.signal, //signal
    strategiesReceived, //successFn, receives resp and data
    auth.newErrorHandler, //error AlerterFn
    "There was an error retrieving user's tender strategies", //error message
    strategiesError, //errorFn (gets error back)
    {
      section: "strategyService -> get strategies",
    } //data (gets handed back to success fn)
  );
};

const strategiesReceived = (resp) => {
  devDebug(`strategyService -> strategiesReceived: `, resp);
  useTenderStrategyStore.getState().setStrategiesStatus("loading");
  let strategies = {};
  for (let strat of resp) {
    strategies[strat.id] = strat;
    if (useAuthStore.getState()?.user?.subscription?.product_type === "only_italy") {
      let newNuts = [];

      for (let nut of strategies[strat.id].nuts) {
        if (nut.includes("IT") && useAuthStore.getState()?.user?.subscription?.product_type === "only_italy") {
          newNuts.push(nut);
        }
      }
      strategies[strat.id].nuts = newNuts;
    }
  }
  devDebug(`received the following strategies: `, resp);
  useTenderStrategyStore.getState().setExistingStrategies(strategies);
  useTenderStrategyStore.getState().setEditableStrategiesWithExistingStrategies("tender_strategy");
  useDefaultStrategyStore.getState().compileDefaultStrategies(strategies);
  useTabStore.getState().verifyTabsExist("tender_strategy");
  useMapsStore.getState().setNUTSMapStage("initialising");
  useMapsStore.getState().verifyMapDataExists();
  useCPVStore.getState().verifyCPVDataExists();
  useCPVStore.getState().setCPVStage("initial");
  useMapsStore.getState().setNUTSMapStage("reloading");
  useTenderStrategyStore.getState().setStrategiesStatus("loaded");
};

const strategiesError = (resp) => {
  devDebug(`strategyService -> strategiesError: ${resp.message}`);
};

export const reloadStrategies = (auth, controller) => {
  useTenderStrategyStore.getState().setStrategiesStatus("reloading");
  devDebug(`strategyService -> reloading strategies`);
  auth.apiService.axiosGET(
    `user/preferences/strategies/`, //endpoint
    controller.signal, //signal
    reloadStrategiesReceived, //successFn, receives resp and data
    auth.newErrorHandler, //error AlerterFn
    "There was an error retrieving user's tender strategies", //error message
    strategiesError, //errorFn (gets error back)
    {
      section: "strategyService -> get strategies",
    } //data (gets handed back to success fn)
  );
};

const reloadStrategiesReceived = (resp) => {
  devDebug(`strategyService -> reloadStrategiesReceived: `, resp);
  useMapsStore.getState().setNUTSMapStage("reloading");
  //setCPVStage("reloading"); //not currently needed, will be needed when cpv stats added again

  let strategies = {};
  for (let strat of resp) {
    strategies[strat.id] = strat;
    if (useAuthStore.getState()?.user?.subscription?.product_type === "only_italy") {
      let newNuts = [];

      for (let nut of strategies[strat.id].nuts) {
        if (nut.includes("IT") && useAuthStore.getState()?.user?.subscription?.product_type === "only_italy") {
          newNuts.push(nut);
        }
      }
      strategies[strat.id].nuts = newNuts;
    }
  }
  useTenderStrategyStore.getState().setExistingStrategies(strategies);
  useTenderStrategyStore.getState().setEditableStrategies(strategies, "tender_strategy");
  useDefaultStrategyStore.getState().compileDefaultStrategies(strategies);
  useTabStore.getState().verifyTabsExist("tender_strategy");
  useMapsStore.getState().verifyMapDataExists();
  useCPVStore.getState().verifyCPVDataExists();
  devDebug(`strategyService -> setting strategy status to 'loaded'`);
  useTenderStrategyStore.getState().setStrategiesStatus("loaded");
};

export function StrategyService({ children }) {
  //devDebug(`strategyService -> StrategyProvider loaded`);
  const auth = useAuth();

  const [strategiesStatus, triggerStrategyReload] = useTenderStrategyStore((state) => [
    state.strategiesStatus,
    state.triggerStrategyReload,
  ]);

  const [userID] = useAuthStore((state) => [state?.user?.id]);

  React.useEffect(() => {
    const controller = new AbortController();
    const strategiesNeverRequested = useAuthStore.getState().strategiesNeverRequested;
    const setStrategiesNeverRequested = useAuthStore.getState().setStrategiesNeverRequested;
    const setStrategiesStatus = useTenderStrategyStore.getState().setStrategiesStatus;
    if (userID) {
      if (strategiesNeverRequested) {
        devDebug(`strategyService -> strategiesNeverRequested was true, userID is: `, userID);
        setStrategiesStatus("loading");
        getStrategies(auth, controller);
        setStrategiesNeverRequested(false);
      } else {
        devDebug(`strategyService -> strategiesNeverRequested was false, userID is: `, userID);
        getStrategies(auth, controller);

        setStrategiesStatus("loaded");
        setTimeout(() => {
          setStrategiesStatus("loaded");
        }, 1000);
      }
    } else {
      devDebug(`strategyService -> there was no user id, cannot get strategies`);
      setStrategiesStatus("loaded");
      setTimeout(() => {
        setStrategiesStatus("loaded");
      }, 1000);
    }
    return () => {
      controller.abort();
    };
  }, [userID, triggerStrategyReload]);

  return strategiesStatus === "loading" ? ( // screen spinners translate within the component
    <ScreenSpinner copy="Fetching your strategies, one moment..." />
  ) : (
    <div>{children}</div>
  );
}
